import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Menu, X, Sparkles, Gem, MessageCircle, Phone } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { LanguageToggle } from './LanguageToggle';
export function Header() {
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const navItems = [{
    href: '#collection',
    label: t.nav.collection,
    icon: <ShoppingBag className="w-4 h-4" data-id="element-344" />
  }, {
    href: '#ferbytouch',
    label: t.nav.ferbytouch,
    icon: <Gem className="w-4 h-4" data-id="element-345" />
  }, {
    href: '#testimonials',
    label: t.nav.testimonials,
    icon: <MessageCircle className="w-4 h-4" data-id="element-346" />
  }, {
    href: '#footer-contact',
    label: t.nav.contact,
    icon: <Phone className="w-4 h-4" data-id="element-347" />
  }];
  const closeMenu = () => setOpen(false);
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    const id = href.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    closeMenu();
    setTimeout(() => {
      const headerOffset = 88;
      const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }, 50);
  };
  return <header className="fixed top-0 left-0 right-0 z-50 bg-cream-100/80 backdrop-blur-md border-b border-lavender-100 transition-all duration-300" data-id="element-348">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-349">
        <div className="flex justify-between items-center h-20" data-id="element-350">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center" data-id="element-351">
            <a href="#" className="flex items-center gap-3" onClick={closeMenu} data-id="element-352">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-sm" data-id="element-353">
                <img src="https://cdn.magicpatterns.com/uploads/5GKd3WcM4VNULi49z2wXh8/Logo_Final.jpg" alt="FerbyStore Logo" className="w-full h-full object-cover" data-id="element-354" />
              </div>
              <span className="font-serif font-semibold text-xl text-gray-800 hidden sm:block" data-id="element-355">
                FerbyStore
              </span>
            </a>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex space-x-8" data-id="element-356">
            {navItems.map(item => <a key={item.href} href={item.href} onClick={e => handleNavClick(e, item.href)} className="text-gray-600 hover:text-lavender-600 font-medium transition-colors" data-id="element-357">
                {item.label}
              </a>)}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-358">
            <LanguageToggle data-id="element-359" />

            <a href="https://shop.ferbystore.com/" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-full text-white bg-lavender-500 hover:bg-lavender-600 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5" data-id="element-360">
              <ShoppingBag className="w-4 h-4 mr-2" data-id="element-361" />
              {t.nav.shopCta}
            </a>

            <button type="button" onClick={() => setOpen(v => !v)} aria-label={open ? t.nav.closeMenu : t.nav.openMenu} aria-expanded={open} aria-controls="mobile-accordion" className="md:hidden w-11 h-11 rounded-full bg-white border border-lavender-100 shadow-sm flex items-center justify-center text-gray-700 hover:bg-lavender-50 transition-colors" data-id="element-362">
              <AnimatePresence mode="wait" initial={false} data-id="element-363">
                {open ? <motion.span key="close" initial={{
                rotate: -90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: 90,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-364">
                    <X className="w-5 h-5" data-id="element-365" />
                  </motion.span> : <motion.span key="open" initial={{
                rotate: 90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: -90,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-366">
                    <Menu className="w-5 h-5" data-id="element-367" />
                  </motion.span>}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Accordion */}
        <AnimatePresence initial={false} data-id="element-368">
          {open && <motion.div id="mobile-accordion" key="accordion" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} className="md:hidden overflow-hidden" data-id="element-369">
              <nav className="pb-5 pt-2" data-id="element-370">
                <ul className="space-y-1 bg-white rounded-2xl border border-lavender-100 shadow-sm overflow-hidden" data-id="element-371">
                  {navItems.map((item, i) => <motion.li key={item.href} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.05 + i * 0.05,
                duration: 0.25
              }} className="border-b border-lavender-50 last:border-b-0" data-id="element-372">
                      <a href={item.href} onClick={e => handleNavClick(e, item.href)} className="flex items-center gap-4 px-5 py-4 text-gray-700 hover:bg-lavender-50 hover:text-lavender-700 transition-colors group" data-id="element-373">
                        <span className="w-9 h-9 rounded-full bg-cream-50 group-hover:bg-white flex items-center justify-center text-lavender-500 transition-colors flex-shrink-0" data-id="element-374">
                          {item.icon}
                        </span>
                        <span className="font-medium flex-1" data-id="element-375">{item.label}</span>
                        <svg className="w-4 h-4 text-gray-300 group-hover:text-lavender-500 transform group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-376">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-377" />
                        </svg>
                      </a>
                    </motion.li>)}
                </ul>

                <motion.a initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3,
              duration: 0.25
            }} href="#collection" onClick={e => handleNavClick(e, '#collection')} className="mt-4 sm:hidden inline-flex w-full items-center justify-center px-6 py-3.5 text-sm font-medium rounded-full text-white bg-lavender-500 hover:bg-lavender-600 shadow-sm transition-all" data-id="element-378">
                  <Sparkles className="w-4 h-4 mr-2" data-id="element-379" />
                  {t.nav.shopCta}
                </motion.a>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </div>
    </header>;
}